const post = {
  slug: 'youtube-competitor-research',
  cluster: 'topics',
  eyebrow: 'Topics',
  title: 'Studying competitors without copying them',
  description:
    'What to actually look for in other channels — and why copying the thing that made their video work usually fails.',
  keywords: [
    'youtube competitor research', 'competitor analysis youtube', 'what makes videos work',
    'youtube content gap', 'studying successful channels', 'youtube research',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-competitor-research.webp',
    alt: 'A video timeline annotated with where the question is asked and where it is answered.',
  },
  lede:
    'Watching what worked for someone else is useful. Copying it is not, and the reason why is the most useful thing competitor research teaches you.',
  sections: [
    {
      heading: 'Why copying fails',
      body: [
        {
          type: 'para',
          text: 'A video that did well did well for a reason, and the reason is almost never the visible thing.',
        },
        {
          type: 'para',
          text: 'People copy the thumbnail style, the intro cadence, the music, the sentence rhythm. Those are the surface. The video worked because it answered a question people had with material nobody else had assembled — and that is not visible from the outside, which is why it does not get copied.',
        },
        {
          type: 'para',
          text: 'The result is a video that looks like a successful one and performs like a generic one.',
        },
      ],
    },
    {
      heading: 'What to actually extract',
      body: [
        {
          type: 'sub',
          text: 'The question, and where it gets answered',
        },
        {
          type: 'para',
          text: 'Watch the first thirty seconds and write down the question the video posed. Then find where it answered it. That gap — its length and what fills it — is the structural decision that held the audience, and it is transferable to any topic.',
        },
        {
          type: 'sub',
          text: 'What is on screen during the boring parts',
        },
        {
          type: 'para',
          text: 'Anyone can make the exciting sixty seconds look good. Skip to a mid-video explanatory stretch and see what the visuals do. That is where faceless videos are won or lost, and it is the part nobody studies.',
        },
        {
          type: 'sub',
          text: 'The specifics they had',
        },
        {
          type: 'para',
          text: 'Note the names, dates and figures. Then ask where they came from. Frequently the answer is one good source the creator found and everyone else missed — and identifying the *kind* of source is more valuable than the video itself.',
        },
        {
          type: 'sub',
          text: 'What the comments complain about',
        },
        {
          type: 'para',
          text: 'The most underused input available. Comments telling a creator what they got wrong, or what they wish had been covered, are an unserved-demand list written by the exact audience you want.',
        },
      ],
    },
    {
      heading: 'Finding the gap',
      body: [
        {
          type: 'para',
          text: 'Three gaps are reliably findable in any well-covered topic.',
        },
        {
          type: 'list',
          items: [
            '**The follow-up question.** Every video that answers something raises the next thing. That one is usually unmade.',
            '**The specific instance.** The general explainer exists; the particular case that illustrates it often does not.',
            '**The correction.** Where the popular treatment is oversimplified or repeats something untrue, a careful version has an audience — and a durable one, because it stays right.',
          ],
        },
        {
          type: 'para',
          text: 'The correction gap is the strongest and the riskiest: it only works if you are certain, and being confidently wrong while correcting someone is the worst position available.',
        },
      ],
    },
    {
      heading: 'What to ignore',
      body: [
        {
          type: 'list',
          items: [
            '**Subscriber count.** It reflects history, not whether this video is good.',
            '**Production polish.** Motion graphics do not cause views. Plenty of top-performing videos in any niche are visually plain.',
            '**Upload frequency.** Their cadence tells you about their resources, not about what you should do.',
            '**Anything from more than two years ago.** What surfaced then is not what surfaces now.',
          ],
        },
      ],
    },
    {
      heading: 'Do this once per topic, not continuously',
      body: [
        {
          type: 'para',
          text: 'Twenty minutes before writing, to establish what the standard treatment is so you can avoid making it. That is the whole job.',
        },
        {
          type: 'para',
          text: 'Watching competitors continuously is a common failure mode: it converges your channel toward theirs, which is the opposite of what differentiates a small channel.',
        },
      ],
    },
  ],
  related: ['youtube-topic-research', 'youtube-video-ideas-that-work', 'youtube-title-and-thumbnail'],
};

export default post;
